import { Injectable } from '@angular/core';
import { Action, State, StateContext, StateToken, Store } from '@ngxs/store';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { NavController, ToastController } from '@ionic/angular';
import { produce } from 'immer';
import _ from 'lodash';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { combineLatest, from } from 'rxjs';

import { PosManagerServiceState } from './pos-services-manager/pos-services.state';
import { PosScheduleState } from './pos-schedules/pos-schedules.state';
import { NormalizrHelper } from 'src/app/helpers';
import {
  LocationType,
  MacroGridView,
  NormalizedModifierPromptOption,
  NormalizedObject,
  PopularItem,
  SalesByDinerTypeResponse,
  SalesByMenuCategoryResponse,
  SalesByPaymentTypeResponse,
  SalesByPosOperatorsResponse,
  TotalsResponse
} from 'src/app/models';
import { NormalizedModifierPrompt } from 'src/app/models/normalized-models/pos-manager/normalized-modifier-prompt.model';
import { ModalService, PosManagerService } from 'src/app/services';
import { ExceptionError, HttpRequestError } from '../error/error.action';
import {
  ClearSelectedModifierPrompt,
  CreateModifierPrompt,
  CreateModifierPromptSuccess,
  CreateOrUpdateProductionAreaOverride,
  CreateOrUpdateProductionAreaOverrideSuccess,
  DeleteModifierPrompt,
  DeleteProductionAreaOverride,
  DeleteProductionAreaOverrideSuccess,
  GetEmbeddedDashboardUrl,
  GetModifierPrompts,
  GetModifierPromptsSuccess,
  GetProductionAreaOverrides,
  GetProducts,
  GetRealtimeInfo,
  GetRealtimeMostPopularItemsSuccess,
  GetRealtimeSalesByDinerTypeSuccess,
  GetRealtimeSalesByMenuCategory,
  GetRealtimeSalesByMenuCategorySuccess,
  GetRealtimeSalesByPaymentTypeSuccess,
  GetRealtimeSalesByPosOperators,
  GetRealtimeSalesByPosOperatorsSuccess,
  GetRealtimeTotalsSuccess,
  GetSharedObjects,
  PosManagerBackToPos,
  SelectModifierPrompt,
  SelectModifierPromptError,
  UpdateModifierPrompt,
  UpdateModifierPromptSuccess
} from './pos-manager.action';
import {
  ModifierPromptOption,
  ModifierPromptOptionCreateRequest,
  ModifierPromptOptionEditRequest,
  ModifierPromptRequest,
  PosManagerSharedObjects,
  PosQuantityRestriction,
  ProductionAreaOverride,
  SharedObject
} from 'src/app/models/pos-manager.model';
import { PageConstant, PagePath } from 'src/app/constants';
import { TempLogout } from '../app/app.action';
import { LOCATION_STATE_TOKEN } from 'src/app/store/location/location.state';
import { TICKETS_STATE_TOKEN } from 'src/app/store/tickets/tickets.state';
import { UpdateMacroGridView } from 'src/app/store/menu/menu.action';
import { ExitTakeOutDelivery } from 'src/app/store/takeout-delivery/takeout-delivery.action';
import { ClosingTheDayState } from './closing-the-day/closing-the-day.state';
import { PosReportsState } from './pos-reports/pos-reports.state';
import { NavigateService } from 'src/app/services/navigate.service';

export interface ModifierPromptsData {
  loaded: boolean;
  items: NormalizedObject<NormalizedModifierPrompt, number>;
  modifierPromptOptions: NormalizedObject<
    NormalizedModifierPromptOption,
    number
  >;
  selectedModifierPromptId: number;
  products: SharedObject[];
  quantityRestrictions: PosQuantityRestriction[];
}

export interface RealtimeInfoData {
  totals: TotalsResponse;
  salesByPaymentType: SalesByPaymentTypeResponse;
  salesByDinerType: SalesByDinerTypeResponse;
  mostPopularItems: PopularItem[];
  totalSalesByMenuCategory?: SalesByMenuCategoryResponse;
  totalSalesByPosOperators?: SalesByPosOperatorsResponse;
}

export interface PosManagerStateModel {
  modifierPrompts: ModifierPromptsData;
  sharedObjects: PosManagerSharedObjects;
  embeddedDashboardUrl: string;
  overrides: ProductionAreaOverride[];
  realtimeInfo: RealtimeInfoData;
}

export const POS_MANAGER_STATE_TOKEN = new StateToken<PosManagerStateModel>(
  'posManager'
);

@State({
  name: POS_MANAGER_STATE_TOKEN,
  defaults: {
    modifierPrompts: {
      loaded: false,
      items: {
        byId: null,
        allIds: []
      },
      modifierPromptOptions: {
        byId: null,
        allIds: []
      },
      selectedModifierPromptId: null,
      products: null,
      quantityRestrictions: null
    },
    sharedObjects: null,
    embeddedDashboardUrl: null,
    overrides: [],
    realtimeInfo: {
      totals: {
        total_orders: null,
        total_sales: null
      },
      salesByPaymentType: {
        cash: {
          total_sales_amount: null,
          total_sales_count: null
        },
        bill_to_room: {
          total_sales_amount: null,
          total_sales_count: null
        },
        credit_card: {
          total_sales_amount: null,
          total_sales_count: null
        },
        gift: {
          total_sales_amount: null,
          total_sales_count: null
        },
        charge_account: {
          total_sales_amount: null,
          total_sales_count: null
        },
        meal_plan: {
          total_sales_amount: null,
          total_sales_count: null
        }
      },
      salesByDinerType: {
        guest: {
          total_sales_amount: null,
          total_sales_count: null
        },
        resident: {
          total_sales_amount: null,
          total_sales_count: null
        },
        staff: {
          total_sales_amount: null,
          total_sales_count: null
        }
      },
      mostPopularItems: [],
      totalSalesByPosOperators: null
    }
  },
  children: [
    PosManagerServiceState,
    PosScheduleState,
    ClosingTheDayState,
    PosReportsState
  ]
})
@Injectable()
export class PosManagerState {
  constructor(
    private readonly posManagerService: PosManagerService,
    private readonly modalService: ModalService,
    private readonly navController: NavController,
    private readonly toastCtrl: ToastController,
    private readonly store: Store,
    private readonly router: Router,
    private readonly translocoService: TranslocoService,
    private readonly navService: NavigateService
  ) {}

  @Action(GetSharedObjects)
  getSharedObjects(ctx: StateContext<PosManagerStateModel>) {
    return this.posManagerService.getSharedObjects().pipe(
      tap((sharedObjects) => ctx.patchState({ sharedObjects })),
      catchError(() =>
        ctx.dispatch(
          new HttpRequestError(
            this.translocoService.translate(
              'errors.errorFetchingPosManagerSharedObjects'
            )
          )
        )
      )
    );
  }

  @Action(GetEmbeddedDashboardUrl)
  getEmbeddedDashboardUrl(ctx: StateContext<PosManagerStateModel>) {
    return this.posManagerService.getEmbeddedDashboardUrl().pipe(
      tap((url) => ctx.patchState({ embeddedDashboardUrl: url })),
      catchError(() =>
        ctx.dispatch(
          new HttpRequestError(
            this.translocoService.translate(
              'errors.errorFetchingEmbeddedDashboardUrl'
            )
          )
        )
      )
    );
  }

  @Action(GetModifierPrompts)
  getModifierPrompts(ctx: StateContext<PosManagerStateModel>) {
    const state = ctx.getState();

    //Set back to not loaded if loading again
    if (state.modifierPrompts.loaded) {
      ctx.setState(
        produce((draft: PosManagerStateModel) => {
          //Clear all items when re-downloading
          draft.modifierPrompts.items.byId = null;
          draft.modifierPrompts.items.allIds = [];
          draft.modifierPrompts.modifierPromptOptions.allIds = [];
          draft.modifierPrompts.modifierPromptOptions.byId = null;
          draft.modifierPrompts.loaded = false;
        })
      );
    }

    return this.posManagerService.getModifierPrompts().pipe(
      switchMap((modifierPrompts) =>
        ctx.dispatch(new GetModifierPromptsSuccess(modifierPrompts))
      ),
      catchError(() =>
        ctx.dispatch(
          new HttpRequestError(
            this.translocoService.translate(
              'errors.errorFetchingModifierPrompts'
            )
          )
        )
      ),
      tap(() => {
        ctx.setState(
          produce((draft: PosManagerStateModel) => {
            draft.modifierPrompts.loaded = true;
          })
        );
      })
    );
  }

  @Action(GetModifierPromptsSuccess)
  getModifierPromptsSuccess(
    ctx: StateContext<PosManagerStateModel>,
    { modifierPrompts }: GetModifierPromptsSuccess
  ) {
    const normalizedEntities =
      NormalizrHelper.normalizeModifierPrompts(modifierPrompts).entities;

    if (normalizedEntities && normalizedEntities.modifierPrompts) {
      ctx.setState(
        produce((draft: PosManagerStateModel) => {
          draft.modifierPrompts.items = {
            byId: {
              ...draft.modifierPrompts.items.byId,
              ...normalizedEntities.modifierPrompts
            },
            allIds: _.union(
              draft.modifierPrompts.items.allIds,
              Object.keys(normalizedEntities.modifierPrompts).map((key) => +key)
            )
          };

          if (normalizedEntities.modifierPromptOptions) {
            draft.modifierPrompts.modifierPromptOptions = {
              byId: {
                ...draft.modifierPrompts.modifierPromptOptions.byId,
                ...normalizedEntities.modifierPromptOptions
              },
              allIds: _.union(
                draft.modifierPrompts.modifierPromptOptions.allIds,
                Object.keys(normalizedEntities.modifierPromptOptions).map(
                  (key) => +key
                )
              )
            };
          }
        })
      );
    }
  }

  @Action(SelectModifierPrompt)
  selectModifierPrompt(
    ctx: StateContext<PosManagerStateModel>,
    { id }: SelectModifierPrompt
  ) {
    const state = ctx.getState();

    //Check if ID exists on modifier prompt list
    if (!state.modifierPrompts.items.allIds.includes(id)) {
      ctx.dispatch(new SelectModifierPromptError());
    } else {
      ctx.setState(
        produce((draft: PosManagerStateModel) => {
          draft.modifierPrompts.selectedModifierPromptId = id;
        })
      );
    }
  }

  @Action(ClearSelectedModifierPrompt)
  clearSelectedModifierPrompt(ctx: StateContext<PosManagerStateModel>) {
    ctx.setState(
      produce((draft: PosManagerStateModel) => {
        draft.modifierPrompts.selectedModifierPromptId = null;
      })
    );
  }

  @Action(CreateModifierPrompt)
  createModifierPrompt(
    ctx: StateContext<PosManagerStateModel>,
    { name, modifierPromptOptions }: CreateModifierPrompt
  ) {
    const data: ModifierPromptRequest = {
      question_label: name,
      modifier_prompt_options_attributes:
        this.transformModifierPromptOptionsToRequest(modifierPromptOptions)
    };

    return this.posManagerService.createModifierPrompt(data).pipe(
      tap(() => ctx.dispatch(new CreateModifierPromptSuccess())),
      catchError(() =>
        ctx.dispatch(
          new ExceptionError(
            this.translocoService.translate(
              'errors.errorCreatingModifierPrompt'
            )
          )
        )
      )
    );
  }

  @Action(CreateModifierPromptSuccess)
  createModifierPromptSuccess() {
    this.navController.back();
  }

  @Action(UpdateModifierPrompt)
  updateModifierPrompt(
    ctx: StateContext<PosManagerStateModel>,
    { id, name, modifierPromptOptions }: UpdateModifierPrompt
  ) {
    const data: ModifierPromptRequest = {
      question_label: name,
      modifier_prompt_options_attributes:
        this.transformModifierPromptOptionsToRequest(modifierPromptOptions)
    };

    return this.posManagerService.updateModifierPrompt(id, data).pipe(
      tap(() => ctx.dispatch(new UpdateModifierPromptSuccess())),
      catchError(() =>
        ctx.dispatch(
          new ExceptionError(
            this.translocoService.translate(
              'errors.errorUpdatingModifierPrompt'
            )
          )
        )
      )
    );
  }

  @Action(UpdateModifierPromptSuccess)
  updateModifierPromptSuccess(ctx: StateContext<PosManagerStateModel>) {
    ctx.setState(
      produce((draft: PosManagerStateModel) => {
        draft.modifierPrompts.selectedModifierPromptId = null;
        draft.modifierPrompts.products = null;
      })
    );

    this.navController.navigateRoot(
      PageConstant.POS_MANAGER_MODIFIER_PROMPTS_PAGE
    );
  }

  @Action(DeleteModifierPrompt)
  deleteModifierPrompt(
    ctx: StateContext<PosManagerStateModel>,
    { id }: DeleteModifierPrompt
  ) {
    return this.posManagerService.deleteModifierPrompt(id).pipe(
      tap((success) => {
        if (success) {
          ctx.setState(
            produce((draft: PosManagerStateModel) => {
              //Remove modifier prompt from store
              if (_.has(draft.modifierPrompts.items.byId, id)) {
                delete draft.modifierPrompts.items.byId[id];
                _.remove(
                  draft.modifierPrompts.items.allIds,
                  (item) => item === id
                );

                //Unselect deleted modifier prompt
                draft.modifierPrompts.selectedModifierPromptId = null;
              }
            })
          );

          this.navController.navigateRoot(
            PageConstant.POS_MANAGER_MODIFIER_PROMPTS_PAGE
          );
        }
      }),
      catchError(() =>
        ctx.dispatch(
          new ExceptionError(
            this.translocoService.translate(
              'errors.errorDeletingModifierPrompt',
              { id }
            )
          )
        )
      )
    );
  }

  @Action(SelectModifierPromptError)
  selectModifierPromptError(ctx: StateContext<PosManagerStateModel>) {
    ctx.dispatch(
      new ExceptionError(
        this.translocoService.translate('errors.modifierPromptNotFound')
      )
    );
    this.navController.back();
  }

  @Action(GetProducts)
  getProducts(ctx: StateContext<PosManagerStateModel>) {
    return this.posManagerService.getProducts().pipe(
      map(
        (products) =>
          products.map((p) => ({ id: p.id, name: p.name })) as SharedObject[]
      ),
      tap((mappedProducts) => {
        ctx.setState(
          produce((draft: PosManagerStateModel) => {
            draft.modifierPrompts.products = mappedProducts;
          })
        );
      }),
      catchError(() =>
        ctx.dispatch(
          new ExceptionError(
            this.translocoService.translate('errors.errorFetchingProducts')
          )
        )
      )
    );
  }

  @Action(PosManagerBackToPos)
  posManagerBackToPos(
    _ctx: StateContext<PosManagerStateModel>,
    payload: PosManagerBackToPos
  ) {
    const locationType =
      this.store.selectSnapshot(LOCATION_STATE_TOKEN).location.type;
    const isDiningRoom: boolean = locationType === LocationType.DiningRoom;
    const isSelfService: boolean = locationType === LocationType.SelfServe;
    const fromPath: string = payload.route.snapshot.queryParams['from'];
    const isTakeoutDelivery = fromPath.includes(PagePath.TAKEOUT_DELIVERY_PAGE);

    let pageUrl: string | undefined =
      PageConstant.LOCATION_ROOT_URL_MAPPER[locationType];

    if (isSelfService) {
      this.store.dispatch(new TempLogout());
    }

    if (!isTakeoutDelivery) {
      console.log('HP -> ', fromPath);
      this.navService.returnToPOSLocationRoot(locationType);
      return;
    }

    // POSV3-1499 Takeout order - When returning back from POS Manager, I should expect to be on the ticket order screen that I was on before I went to POS Manager.

    if (isTakeoutDelivery) {
      const selectedTicketId =
        this.store.selectSnapshot(TICKETS_STATE_TOKEN).selectedTicket.data;

      if (selectedTicketId && isDiningRoom) {
        this.store.dispatch(new UpdateMacroGridView(MacroGridView.MAIN));
        pageUrl = `${PageConstant.TAKEOUT_DELIVERY_VIEW_ORDER_PAGE(
          isDiningRoom
        )}/${selectedTicketId}`;
      } else {
        // only disabled the takeout delivery mode
        pageUrl = isDiningRoom
          ? PageConstant.TAKEOUT_DELIVERY_PAGE(isDiningRoom)
          : undefined;
        this.store.dispatch(new ExitTakeOutDelivery());
      }
    }

    console.log('HP: ', fromPath, pageUrl);
    if (fromPath) {
      this.navController.navigateRoot(this.router.parseUrl(fromPath));
    } else if (pageUrl) {
      this.navController.navigateRoot(pageUrl, {
        animated: true,
        animationDirection: 'forward'
      });
    } else {
      console.log('HP');
      this.navService.returnToPOSLocationRoot(locationType);
    }
  }

  //Transform modifier prompt option from local format to server format
  private transformModifierPromptOptionsToRequest(
    modifierPromptOptions: ModifierPromptOption[]
  ): (ModifierPromptOptionCreateRequest | ModifierPromptOptionEditRequest)[] {
    return modifierPromptOptions.map((o) => {
      const tmp:
        | ModifierPromptOptionCreateRequest
        | ModifierPromptOptionEditRequest = {
        option_type: o.option_type,
        sort_order: o.sort_order
      };

      if (o.id) {
        (<ModifierPromptOptionEditRequest>tmp).id = o.id;

        if (o._destroy) {
          (<ModifierPromptOptionEditRequest>tmp)._destroy = true;
        }
      }

      if (o.option_type === 'text') {
        tmp.text_label = o.text_label;
        tmp.added_price = o.added_price;
      } else if (o.option_type === 'category') {
        tmp.category_id = o.category.id;
      } else if (o.option_type === 'pos_product') {
        tmp.pos_product_id = o.pos_product.id;
      }
      tmp.modifier_color = o.modifier_color;

      return tmp;
    }) as (
      | ModifierPromptOptionCreateRequest
      | ModifierPromptOptionEditRequest
    )[];
  }

  @Action(GetProductionAreaOverrides)
  getProductionAreaOverrides(
    ctx: StateContext<PosManagerStateModel>,
    { posLocationId }: GetProductionAreaOverrides
  ) {
    return this.posManagerService.getProductionAreaOverrides().pipe(
      tap((responseOverrides) => {
        ctx.setState(
          produce((draft: PosManagerStateModel) => {
            draft.overrides = responseOverrides.filter(
              (override) => override.pos_location_id === posLocationId
            );
          })
        );
      }),
      catchError(() =>
        ctx.dispatch(
          new ExceptionError(
            this.translocoService.translate(
              'errors.errorFetchingProductionAreaOverrides'
            )
          )
        )
      )
    );
  }

  @Action(CreateOrUpdateProductionAreaOverride)
  createProductionAreaOverride(
    ctx: StateContext<PosManagerStateModel>,
    { payload }: CreateOrUpdateProductionAreaOverride
  ) {
    // this.modalService.showLoadingModal('Saving...');
    const isAlreadyOverridden = !!ctx
      .getState()
      .overrides.find((override) => override.food_id === payload.food_id);
    return from(this.modalService.showLoadingModal('Saving...')).pipe(
      switchMap((loadingModal) =>
        this.posManagerService
          .createOrUpdateProductionAreaOverride(payload, isAlreadyOverridden)
          .pipe(
            tap((responseOverride) => {
              void this.modalService.closeLoadingModal(loadingModal.id);
              ctx.dispatch(
                new CreateOrUpdateProductionAreaOverrideSuccess(
                  responseOverride
                )
              );
            }),
            catchError(() =>
              ctx.dispatch(
                new ExceptionError(
                  'There was an error while creating production area override'
                )
              )
            )
          )
      )
    );
  }

  @Action(CreateOrUpdateProductionAreaOverrideSuccess)
  async createProductionAreaOverrideSuccess(
    ctx: StateContext<PosManagerStateModel>,
    { response }: CreateOrUpdateProductionAreaOverrideSuccess
  ) {
    const existingOverride = ctx
      .getState()
      .overrides.find((override) => override.food_id === response.food_id);
    ctx.setState(
      produce((draft: PosManagerStateModel) => {
        if (existingOverride) {
          const index = draft.overrides.findIndex(
            (override) => override.food_id === response.food_id
          );
          draft.overrides[index] = response;
        } else {
          draft.overrides = [...draft.overrides, response];
        }
      })
    );
    this.showProductionAreaToast();
  }

  @Action(DeleteProductionAreaOverride)
  deleteProductionAreaOverride(
    ctx: StateContext<PosManagerStateModel>,
    { id }: DeleteProductionAreaOverride
  ) {
    return from(this.modalService.showLoadingModal('Saving...')).pipe(
      switchMap((loadingModal) =>
        this.posManagerService.deleteProductionAreaOverrides(id).pipe(
          tap(() => ctx.dispatch(new DeleteProductionAreaOverrideSuccess(id))),
          catchError(() =>
            ctx.dispatch(
              new ExceptionError(
                'There was an error while deleting production area override'
              )
            )
          ),
          finalize(() => this.modalService.closeLoadingModal(loadingModal.id))
        )
      )
    );
  }

  @Action(DeleteProductionAreaOverrideSuccess)
  deleteProductionAreaOverrideSuccess(
    ctx: StateContext<PosManagerStateModel>,
    { id }: DeleteProductionAreaOverrideSuccess
  ) {
    ctx.setState(
      produce((draft: PosManagerStateModel) => {
        _.remove(draft.overrides, (item) => item.id === id);
      })
    );
    this.showProductionAreaToast();
  }

  @Action(GetRealtimeInfo)
  getRealtimeInfo(
    ctx: StateContext<PosManagerStateModel>,
    { params }: GetRealtimeInfo
  ) {
    return combineLatest([
      this.posManagerService.getRealtimeTotals(params),
      this.posManagerService.getRealtimeSalesByPaymentType(params),
      this.posManagerService.getRealtimeSalesByDinerType(params),
      this.posManagerService.getRealtimeMostPopularItems(params)
    ]).pipe(
      tap(
        ([
          totalsResponse,
          salesByPaymentResponse,
          salesByDinerResponse,
          mostPopularItemsResponse
        ]) => {
          ctx.dispatch([
            new GetRealtimeTotalsSuccess(totalsResponse),
            new GetRealtimeSalesByPaymentTypeSuccess(salesByPaymentResponse),
            new GetRealtimeSalesByDinerTypeSuccess(salesByDinerResponse),
            new GetRealtimeMostPopularItemsSuccess(
              mostPopularItemsResponse.most_popular_items
            )
          ]);
        }
      ),
      catchError(() => {
        this.modalService.closeAllModals();
        return ctx.dispatch(
          new HttpRequestError(
            this.translocoService.translate('errors.errorFetchingRealtimeInfo')
          )
        );
      })
    );
  }

  @Action(GetRealtimeTotalsSuccess)
  getRealtimeTotalsSuccess(
    ctx: StateContext<PosManagerStateModel>,
    { response }: GetRealtimeTotalsSuccess
  ) {
    ctx.setState(
      produce((draft: PosManagerStateModel) => {
        draft.realtimeInfo = {
          ...draft.realtimeInfo,
          totals: response
        };
      })
    );
  }

  @Action(GetRealtimeSalesByPaymentTypeSuccess)
  getRealtimeSalesByPaymentTypeSuccess(
    ctx: StateContext<PosManagerStateModel>,
    { response }: GetRealtimeSalesByPaymentTypeSuccess
  ) {
    ctx.setState(
      produce((draft: PosManagerStateModel) => {
        draft.realtimeInfo = {
          ...draft.realtimeInfo,
          salesByPaymentType: response
        };
      })
    );
  }

  @Action(GetRealtimeSalesByDinerTypeSuccess)
  getRealtimeSalesByDinerTypeSuccess(
    ctx: StateContext<PosManagerStateModel>,
    { response }: GetRealtimeSalesByDinerTypeSuccess
  ) {
    ctx.setState(
      produce((draft: PosManagerStateModel) => {
        draft.realtimeInfo = {
          ...draft.realtimeInfo,
          salesByDinerType: response
        };
      })
    );
  }

  @Action(GetRealtimeMostPopularItemsSuccess)
  getRealtimeMostPopularItemsSuccess(
    ctx: StateContext<PosManagerStateModel>,
    { response }: GetRealtimeMostPopularItemsSuccess
  ) {
    ctx.setState(
      produce((draft: PosManagerStateModel) => {
        draft.realtimeInfo = {
          ...draft.realtimeInfo,
          mostPopularItems: [...response].sort(
            (a, b) => (b.order_count ?? 0) - (a.order_count ?? 0)
          )
        };
      })
    );
  }

  @Action(GetRealtimeSalesByMenuCategory)
  getRealtimeSalesByMenuCategory(
    ctx: StateContext<PosManagerStateModel>,
    { params }: GetRealtimeSalesByMenuCategory
  ) {
    return this.posManagerService.getRealtimeSalesByMenuCategory(params).pipe(
      tap((response) =>
        ctx.dispatch(new GetRealtimeSalesByMenuCategorySuccess(response))
      ),
      catchError(() =>
        ctx.dispatch(
          new HttpRequestError(
            this.translocoService.translate(
              'errors.errorFetchingRealtimeSalesByMenuCategory'
            )
          )
        )
      )
    );
  }

  @Action(GetRealtimeSalesByMenuCategorySuccess)
  getRealtimeSalesByMenuCategorySuccess(
    ctx: StateContext<PosManagerStateModel>,
    { response }: GetRealtimeSalesByMenuCategorySuccess
  ) {
    ctx.setState(
      produce((draft: PosManagerStateModel) => {
        draft.realtimeInfo = {
          ...draft.realtimeInfo,
          totalSalesByMenuCategory: response
        };
      })
    );
  }

  @Action(GetRealtimeSalesByPosOperators)
  getRealtimeSalesByPosOperators(
    ctx: StateContext<PosManagerStateModel>,
    { params }: GetRealtimeSalesByPosOperators
  ) {
    return this.posManagerService.getRealtimeSalesByPosOperators(params).pipe(
      tap((response) =>
        ctx.dispatch(new GetRealtimeSalesByPosOperatorsSuccess(response))
      ),
      catchError(() =>
        ctx.dispatch(
          new HttpRequestError(
            this.translocoService.translate(
              'errors.errorFetchingRealtimeSalesByPosOperators'
            )
          )
        )
      )
    );
  }

  @Action(GetRealtimeSalesByPosOperatorsSuccess)
  getRealtimeSalesByPosOperatorsSuccess(
    ctx: StateContext<PosManagerStateModel>,
    { response }: GetRealtimeSalesByPosOperatorsSuccess
  ) {
    ctx.setState(
      produce((draft: PosManagerStateModel) => {
        draft.realtimeInfo = {
          ...draft.realtimeInfo,
          totalSalesByPosOperators: response
        };
      })
    );
  }

  async showProductionAreaToast() {
    const toast = await this.toastCtrl.create({
      color: 'primary',
      duration: 2000,
      message: this.translocoService.translate(
        'messages.productionAreaSavedSuccess'
      ),
      position: 'top'
    });
    toast.present();
  }
}
